import { createSelector } from 'reselect'
import { buildAccommodationCartEvaluationItems } from 'businessTraveller/utils/buildAccommodationCartEvaluationItems'
import { buildFlightCartEvaluationItems } from 'businessTraveller/utils/buildFlightCartEvaluationItems'
import { buildCarHireCartEvaluationItems } from 'businessTraveller/utils/buildCarHireCartEvaluationItems'
import { selectTravellerCount } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import { selectIsLebtItemPoliciesEnabled } from 'businessTraveller/selectors/itemPolicy/selectIsLebtItemPoliciesEnabled'
import {
  getFlightDateChangeCartEvaluationItems,
  selectIsFlightDateChangeCartEvaluation,
} from 'businessTraveller/selectors/itemPolicy/getFlightDateChangeCartEvaluationItems'
import { checkoutAccommodationOfferView } from './view/accommodation'
import { getFlightItemsView } from './view/flights'
import { getCarHireItemsView } from './view/carHire'
import type { CartLineContext } from 'businessTraveller/utils/resolveCartLineTravellers'
import type { CartEvaluationItemInputWire } from 'businessTraveller/api/requestPostCartEvaluation'

// The POST /cart/evaluation request items, built from the checkout item views.
// hasRequiredData: every item view has hydrated, so an empty items list means an empty cart, not one still loading.
export const getCartEvaluationItems = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.businessTraveller.selectedTravellerEmployeeIds,
  (state: App.State) => state.businessTraveller.selectedTravellerGuestCount,
  (state: App.State) => selectTravellerCount(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getCarHireItemsView(state),
  (state: App.State) => selectIsFlightDateChangeCartEvaluation(state),
  (state: App.State) => getFlightDateChangeCartEvaluationItems(state),
  (
    currencyCode,
    selectedEmployeeIds,
    selectedGuestCount,
    travellerCount,
    accommodationOfferViews,
    flightItemsViews,
    carHireItemsViews,
    isFlightDateChangeEvaluation,
    flightDateChangeItems,
  ): App.WithDataStatus<Array<CartEvaluationItemInputWire>> => {
    if (isFlightDateChangeEvaluation) {
      return flightDateChangeItems
    }

    const context: CartLineContext = {
      currencyCode,
      selectedEmployeeIds,
      selectedGuestCount,
      travellerCount: travellerCount ?? 1,
    }

    return {
      hasRequiredData:
        accommodationOfferViews.hasRequiredData &&
        flightItemsViews.hasRequiredData &&
        carHireItemsViews.hasRequiredData,
      data: [
        ...buildAccommodationCartEvaluationItems(accommodationOfferViews, context),
        ...buildFlightCartEvaluationItems(flightItemsViews, context),
        ...buildCarHireCartEvaluationItems(carHireItemsViews, context),
      ],
    }
  },
)

// The evaluation is still in flight or yet to start; a failed evaluation is handled separately
export const selectIsAwaitingCartEvaluation = createSelector(
  (state: App.State) => selectIsLebtItemPoliciesEnabled(state),
  (state: App.State) => state.businessTraveller.currentBusinessAccount?.business?.id,
  (state: App.State) => state.businessTraveller.currentBusinessAccount?.employee?.id,
  (state: App.State) => state.checkout.isCartRestored,
  (state: App.State) => getCartEvaluationItems(state),
  (state: App.State) => state.businessTraveller.cartEvaluation.status,
  (
    isItemPoliciesEnabled,
    businessId,
    bookingManagerEmployeeId,
    isCartRestored,
    cartEvaluationItems,
    status,
  ) =>
    isItemPoliciesEnabled &&
    !!businessId &&
    !!bookingManagerEmployeeId &&
    (!isCartRestored || !cartEvaluationItems.hasRequiredData || cartEvaluationItems.data.length > 0) &&
    status === 'pending',
)
